import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import {
  Users,
  TrendingUp,
  DollarSign,
  Shield,
  Download,
  Filter,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  RefreshCw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import InlineFilter from '../../components/ui/inline-filter';
import { ADMIN_ACCOUNT_FILTER_FIELDS } from '../../constants/filters';
import {
  getStatusColor,
  capitalizeFirst,
  exportTableToExcel,
} from '../../utils';
import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import { MT5AccountUser } from '../../utils/types';
import { mt5API } from '../../services/api';

import AccountDetailsDrawer from '../../components/accounts/AccountDetailsDrawer';

const AdminAccounts = () => {
  const [accounts, setAccounts] = useState<MT5AccountUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<MT5AccountUser | null>(
    null
  );
  const [showViewDrawer, setShowViewDrawer] = useState<boolean>(false);
  const [loadingViewDetails, setLoadingViewDetails] = useState<boolean>(false);

  const filterFields = ADMIN_ACCOUNT_FILTER_FIELDS;

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await mt5API.getUsers();
      const data = response?.data || response;
      if (Array.isArray(data)) {
        setAccounts(data);
      }
    } catch (error) {
      console.error('Error fetching admin accounts from MT5 API:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleSearchReset = () => {
    setSearchValue('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleViewAccount = async (login: number) => {
    const foundAccount = accounts.find(acc => acc.Login === login);
    setSelectedAccount(foundAccount || ({ Login: login } as MT5AccountUser));
    setShowViewDrawer(true);
    setLoadingViewDetails(true);
    try {
      const freshUserData = await mt5API.getUserByLogin(login);
      if (freshUserData) {
        setSelectedAccount(freshUserData);
      }
    } catch (error) {
      console.error('Error fetching account details:', error);
    } finally {
      setLoadingViewDetails(false);
    }
  };

  const handleEditAccount = (login: number) => {
    console.log('Edit account:', login);
  };

  const handleSuspendAccount = (login: number) => {
    console.log('Suspend account:', login);
  };

  const handleDeleteAccount = (login: number) => {
    console.log('Delete account:', login);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const handleRefresh = () => {
    fetchAccounts();
  };

  const handleExportData = () => {
    exportTableToExcel(filteredAccounts, columns, 'admin-mt5-accounts');
  };

  const filteredAccounts = accounts.filter(account => {
    const loginStr = String(account.Login || (account as any).mt5Login || '');
    const nameStr = (account.Name || (account as any).name || '').toLowerCase();
    const groupStr = (
      account.Group ||
      (account as any).group ||
      ''
    ).toLowerCase();
    const emailStr = (
      account.Email ||
      (account as any).email ||
      ''
    ).toLowerCase();
    const countryStr = (
      account.Country ||
      (account as any).country ||
      ''
    ).toLowerCase();
    const statusStr = (
      account.Status ||
      (account as any).status ||
      'active'
    ).toLowerCase();
    const levStr = String(account.Leverage || (account as any).leverage || '');
    const balStr = String(account.Balance ?? (account as any).balance ?? 0);
    const credStr = String(account.Credit ?? (account as any).credit ?? 0);
    const sTerm = searchValue.toLowerCase();

    const matchesSearch =
      searchValue === '' ||
      loginStr.includes(sTerm) ||
      nameStr.includes(sTerm) ||
      groupStr.includes(sTerm) ||
      emailStr.includes(sTerm) ||
      countryStr.includes(sTerm) ||
      statusStr.includes(sTerm) ||
      levStr.includes(sTerm) ||
      balStr.includes(sTerm) ||
      credStr.includes(sTerm);

    const matchesStatus =
      !activeFilters.status || statusStr === activeFilters.status.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const totalBalance = accounts.reduce(
    (sum, acc) => sum + Number(acc.Balance ?? (acc as any).balance ?? 0),
    0
  );
  const totalCredit = accounts.reduce(
    (sum, acc) => sum + Number(acc.Credit ?? (acc as any).credit ?? 0),
    0
  );

  const columns: DataTableColumn<MT5AccountUser>[] = [
    {
      id: 'Login',
      header: 'Login',
      accessor: row => {
        const val = row.Login ?? (row as any).mt5Login ?? '-';
        return <span className="font-mono text-sm font-semibold">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Login ?? (row as any).mt5Login ?? 0,
      width: '120px',
    },
    {
      id: 'Name',
      header: 'Name',
      accessor: row => {
        const val = row.Name || (row as any).name || '-';
        return <span className="text-sm font-medium">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Name || (row as any).name || '',
      width: '160px',
    },
    {
      id: 'Group',
      header: 'Group',
      accessor: row => {
        const val = row.Group || (row as any).group || '-';
        return (
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            {val}
          </Badge>
        );
      },
      sortable: true,
      sortValue: row => row.Group || (row as any).group || '',
      width: '180px',
    },
    {
      id: 'Balance',
      header: 'Balance',
      accessor: row => {
        const val = Number(row.Balance ?? (row as any).balance ?? 0);
        return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      },
      sortable: true,
      sortValue: row => Number(row.Balance ?? (row as any).balance ?? 0),
      width: '120px',
    },
    {
      id: 'Credit',
      header: 'Credit',
      accessor: row => {
        const val = Number(row.Credit ?? (row as any).credit ?? 0);
        return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      },
      sortable: true,
      sortValue: row => Number(row.Credit ?? (row as any).credit ?? 0),
      width: '120px',
    },
    {
      id: 'Leverage',
      header: 'Leverage',
      accessor: row => {
        const val = row.Leverage ?? (row as any).leverage;
        return val !== undefined && val !== null && String(val).trim() !== ''
          ? String(val)
          : '-';
      },
      sortable: true,
      sortValue: row => Number(row.Leverage ?? (row as any).leverage ?? 0),
      width: '100px',
    },
    {
      id: 'Email',
      header: 'Email',
      accessor: row => {
        const val = row.Email || (row as any).email || '-';
        return <span className="text-sm text-gray-500">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Email || (row as any).email || '',
      width: '100px',
    },
    {
      id: 'Country',
      header: 'Country',
      accessor: row => {
        const val = row.Country || (row as any).country || '-';
        return <span className="text-sm">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Country || (row as any).country || '',
      width: '100px',
    },
    {
      id: 'Status',
      header: 'Status',
      accessor: row => {
        const val = row.Status || (row as any).status || 'Active';
        return (
          <Badge className={getStatusColor(val)}>{capitalizeFirst(val)}</Badge>
        );
      },
      sortable: true,
      sortValue: row => row.Status || (row as any).status || '',
      width: '100px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => {
        const loginVal = Number(row.Login ?? (row as any).mt5Login ?? 0);
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleViewAccount(loginVal)}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleEditAccount(loginVal)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSuspendAccount(loginVal)}>
                <Shield className="mr-2 h-4 w-4" />
                Suspend
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onClick={() => handleDeleteAccount(loginVal)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      width: '80px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Admin Accounts Management"
          description="Manage all user trading accounts in the system"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Accounts"
          value={accounts.length}
          change="Across all users"
          icon={Users}
        />

        <StatsCard
          title="Active Accounts"
          value={
            accounts.filter(
              a => (a.Status || 'active').toLowerCase() === 'active'
            ).length
          }
          change="Currently active"
          icon={TrendingUp}
        />

        <StatsCard
          title="Total Balance"
          value={`$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          change="Across all accounts"
          icon={DollarSign}
        />

        <StatsCard
          title="Total Credit"
          value={`$${totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          change="Across all accounts"
          icon={Shield}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>All Accounts</CardTitle>
              <CardDescription>
                Manage all user trading accounts in the system
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowInlineFilters(!showInlineFilters);
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportData}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Accounts"
          />
          {loading ? (
            <LoadingSkeleton type="table" rows={5} cols={9} />
          ) : (
            <DataTable
              columns={columns}
              data={filteredAccounts}
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              searchPlaceholder="Search by Login, Name, Group, Email, Country..."
              initialPageSize={10}
              showColumnVisibility={true}
            />
          )}
        </CardContent>
      </Card>

      <AccountDetailsDrawer
        isOpen={showViewDrawer}
        onClose={() => setShowViewDrawer(false)}
        account={selectedAccount}
        loading={loadingViewDetails}
        title="Account Details"
      />
    </div>
  );
};

export default AdminAccounts;
